import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useNavigation } from '@/contexts/NavigationProvider'
import { useProgress } from '@/contexts/ProgressProvider'
import { useGame } from '@/contexts/GameProvider'
import { useLevelRun } from '@/hooks/useLevelRun'
import { useGameEngine } from '@/hooks/useGameEngine'
import { useCallbackRef } from '@/hooks/useCallbackRef'
import { useAutoFullscreenOnPlay } from '@/hooks/useAutoFullscreenOnPlay'
import { getLevel, isEndlessIndex } from '@/game/levels'
import { BIOMES } from '@/styles/theme'
import { HUD } from '@/components/game/HUD'
import { PowerupDock } from '@/components/game/PowerupDock'
import { TouchControls } from '@/components/game/TouchControls'
import { TutorialOverlay } from '@/components/screens/TutorialOverlay'
import { GameButton } from '@/components/ui/GameButton'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { GameBoard3D } from '@/components/game3d/GameBoard3D'
import { Haptics } from '@/arcade'
import { useGameAudio } from '@/contexts/GameAudioProvider'
import type { Direction } from '@/types/game'
import { findTile, buildGridState, type SlideEvent } from '@/game/engine'
import { COSMETICS, POWERUPS } from '@/game/catalog'
import { getCharacter } from '@/game/characters'
import { aggregateEffects } from '@/game/propEffects'
import {
  pickCommentary,
  INITIAL_COMMENTARY_STATE,
  type CommentaryEventKind,
  type CommentaryLine,
  type CommentaryState,
  type CommentaryTone,
} from '@/game/commentary'
import { getLevelStoryCue } from '@/game/storyBeats'

const DIR_VEC: Record<Direction, [number, number]> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
}

const TONE_COLOR: Record<CommentaryTone, string> = {
  normal: 'var(--text)',
  excited: 'var(--ember)',
  warning: '#ff6b5e',
  achievement: 'var(--coin)',
}

const COIN_VALUE = 10

export function GameScreen() {
  const { levelIndex, go, back, replaceLevel, screen } = useNavigation()
  const paused = screen === 'pause'
  const { save, update, consumeLife, addCoins, addGems } = useProgress()
  const { setLastRun, endlessRun, recordEndlessLevel, endEndlessRun } = useGame()
  const audio = useGameAudio()

  const level = levelIndex != null ? getLevel(levelIndex) : undefined
  const safeLevel = level ?? getLevel(0)!
  const endless = isEndlessIndex(safeLevel.index)
  const biome = BIOMES[safeLevel.biome]
  const settings = save.meta.settings
  const haptics = settings.haptics
  const reducedMotion = settings.reducedMotion
  const graphics = settings.graphics

  const character = getCharacter(save.cosmetics.equippedCharacter)
  const effects = useMemo(
    () => aggregateEffects(save.cosmetics.equippedProps, character),
    [save.cosmetics.equippedProps, character],
  )
  const permMagnet = (save.powerups.perm_magnet ?? 0) > 0
  const permDoubler = (save.powerups.perm_double_coins ?? 0) > 0

  const skinColor = COSMETICS.find((c) => c.id === save.cosmetics.equippedSkin && c.kind === 'skin')?.color
  const emberColor =
    character && character.id !== 'default_hero' ? character.palette.base : (skinColor ?? biome.ember)

  const equippedTrail = COSMETICS.find((c) => c.id === save.cosmetics.equippedTrail && c.kind === 'trail')
  const trailColor = equippedTrail && equippedTrail.id !== 'none' ? equippedTrail.color : null

  const run = useLevelRun(safeLevel)
  const { registerSlide, collectCoin, resolve, reset: resetRun, pause: pauseRun, resume: resumeRun, state } = run

  const [events, setEvents] = useState<{ seq: number; list: SlideEvent[]; dir: [number, number] | null }>({
    seq: 0,
    list: [],
    dir: null,
  })
  const seqRef = useRef(0)
  const [tutorialOpen, setTutorialOpen] = useState(true)
  const [bonkKey, setBonkKey] = useState(0)
  const [hintDir, setHintDir] = useState<Direction | null>(null)
  const [flash, setFlash] = useState<{ kind: 'death' | 'win'; key: number } | null>(null)
  const flashSeqRef = useRef(0)
  const finishedRef = useRef(false)
  const deathTimerRef = useRef(0)

  const bankRef = useRef({ coins: 0, gems: 0 })
  const bonksRef = useRef(0)
  const deathsRef = useRef(0)
  const doubleCoinsRef = useRef(false)
  const scoreSurgeRef = useRef(false)

  const commentaryStateRef = useRef<CommentaryState>({ ...INITIAL_COMMENTARY_STATE })
  const [caption, setCaption] = useState<(CommentaryLine & { key: number }) | null>(null)
  const captionTimerRef = useRef(0)
  const captionSeqRef = useRef(0)

  const homePos = useMemo(() => {
    const s = buildGridState(safeLevel.grid)
    return findTile(s.cells, 'e')
  }, [safeLevel.grid])

  const storyCue = useMemo(() => getLevelStoryCue(safeLevel), [safeLevel])

  const speak = useCallbackRef((kind: CommentaryEventKind) => {
    const picked = pickCommentary(
      kind,
      {
        levelIndex: safeLevel.index,
        slides: state.slides,
        bonks: bonksRef.current,
        coins: state.coinsCollected,
        deaths: deathsRef.current,
        streak: endless ? endlessRun.streak : 0,
      },
      commentaryStateRef.current,
      Date.now(),
    )
    if (!picked) return
    commentaryStateRef.current = picked.state
    captionSeqRef.current += 1
    setCaption({ ...picked.line, key: captionSeqRef.current })
    if (captionTimerRef.current) window.clearTimeout(captionTimerRef.current)
    captionTimerRef.current = window.setTimeout(() => setCaption(null), picked.line.ttl)
  })

  const onEvent = useCallbackRef((ev: SlideEvent) => {
    if (ev.kind === 'coin' || ev.kind === 'gem') {
      if (ev.kind === 'coin') bankRef.current.coins += 1
      else bankRef.current.gems += 1
      collectCoin()
      audio.play(ev.kind === 'gem' ? 'gem' : 'coin')
      if (haptics) Haptics.light()
      const collected = bankRef.current.coins + bankRef.current.gems
      if (collected > 0 && collected % 5 === 0) speak('coin-streak')
    } else if (ev.kind === 'key') {
      audio.play('key')
    } else if (ev.kind === 'unlock' || ev.kind === 'switch') {
      audio.play('unlock')
    } else if (ev.kind === 'shatter') {
      audio.play('explode')
      if (haptics) Haptics.medium()
    } else if (ev.kind === 'teleport') {
      audio.play('teleport')
    }
  })

  const onSlideStart = useCallbackRef((dir: Direction) => {
    registerSlide(dir)
    setHintDir(null)
    audio.play('place')
    if (haptics) Haptics.light()
  })

  const onSlideEnd = useCallbackRef((r: { died: boolean; reachedHome: boolean; hitWall: boolean }) => {
    if (r.hitWall && !r.reachedHome && !r.died) {
      bonksRef.current += 1
      setBonkKey((k) => k + 1)
      audio.play('hit')
      if (haptics) Haptics.hit()
      speak('bonk')
    }
  })

  const coinMultiplier = useCallback(() => {
    let mult = effects.coinMult
    if (permDoubler) mult *= 2
    if (doubleCoinsRef.current) mult *= 2
    return mult
  }, [effects.coinMult, permDoubler])

  const scoreMultiplier = useCallback(() => {
    let mult = effects.scoreMult
    if (scoreSurgeRef.current) mult *= 2
    return mult
  }, [effects.scoreMult])

  const finishHome = useCallbackRef(() => {
    if (finishedRef.current) return
    finishedRef.current = true
    const resolved = resolve(true, scoreMultiplier())
    if (!endless && safeLevel.biome === 'dungeon') audio.bossStinger()
    else audio.play('win')
    if (haptics) Haptics.success()
    speak('win')
    flashSeqRef.current += 1
    setFlash({ kind: 'win', key: flashSeqRef.current })

    const coinsBanked = Math.round(bankRef.current.coins * COIN_VALUE * coinMultiplier())
    const gemsBanked = bankRef.current.gems
    if (coinsBanked > 0) addCoins(coinsBanked)
    if (gemsBanked > 0) addGems(gemsBanked)

    if (endless) {
      const newStreak = endlessRun.streak + 1
      const newScore = endlessRun.score + resolved.score
      recordEndlessLevel(resolved.score, coinsBanked)
      update((s) => ({
        ...s,
        progress: {
          ...s.progress,
          endlessBest: {
            streak: Math.max(s.progress.endlessBest.streak, newStreak),
            score: Math.max(s.progress.endlessBest.score, newScore),
          },
        },
      }))
      if (newStreak % 5 === 0) speak('endless-milestone')
      window.setTimeout(() => replaceLevel(safeLevel.index + 1), 900)
      return
    }

    setLastRun(safeLevel.index, resolved)
    window.setTimeout(() => go('level-complete', { levelIndex: safeLevel.index }), 900)
  })

  const engineRef = useRef<ReturnType<typeof useGameEngine> | null>(null)

  const failLevel = useCallbackRef(() => {
    if (finishedRef.current) return
    audio.play('lose')
    if (haptics) Haptics.heavy()
    deathsRef.current += 1
    speak('hazard-death')
    flashSeqRef.current += 1
    setFlash({ kind: 'death', key: flashSeqRef.current })
    const remaining = save.economy.lives - 1
    consumeLife()
    if (remaining > 0) {
      deathTimerRef.current = window.setTimeout(() => {
        deathTimerRef.current = 0
        bankRef.current = { coins: 0, gems: 0 }
        engineRef.current?.respawn()
        resetRun()
        speak('respawn')
      }, 750)
    } else {
      finishedRef.current = true
      const resolved = resolve(false)
      setLastRun(safeLevel.index, resolved)
      if (endless) {
        endEndlessRun()
        update((s) => ({
          ...s,
          progress: {
            ...s.progress,
            endlessBest: {
              streak: Math.max(s.progress.endlessBest.streak, endlessRun.streak),
              score: Math.max(s.progress.endlessBest.score, endlessRun.score),
            },
          },
        }))
        window.setTimeout(() => go('endless'), 750)
      } else {
        window.setTimeout(() => go('no-lives', { levelIndex: safeLevel.index }), 750)
      }
    }
  })

  const engine = useGameEngine(
    safeLevel,
    {
      onEvent: (ev) => onEvent(ev),
      onSlideStart: (dir) => onSlideStart(dir),
      onSlideEnd: (r) => onSlideEnd(r),
      onWin: () => finishHome(),
      onDeath: () => failLevel(),
    },
    {
      magnetRadius: Math.max(effects.magnetRadius, permMagnet ? 2 : 0),
      speedMult: effects.slideSpeedMult,
      autoShield: effects.oneHitShield,
      autoSlowmo: effects.slowmoTint,
      respawnDelayMs: reducedMotion ? 150 : 420,
    },
  )
  engineRef.current = engine

  const { snapshot } = engine

  useEffect(() => {
    finishedRef.current = false
    bankRef.current = { coins: 0, gems: 0 }
    bonksRef.current = 0
    deathsRef.current = 0
    doubleCoinsRef.current = false
    scoreSurgeRef.current = false
    commentaryStateRef.current = { ...INITIAL_COMMENTARY_STATE }
    const seed = safeLevel.index + 1
    const theme =
      safeLevel.biome === 'lava' || safeLevel.biome === 'dungeon' || safeLevel.difficulty === 'expert'
        ? 'tense'
        : safeLevel.biome === 'cave'
          ? 'cave'
          : safeLevel.biome === 'ice'
            ? 'ice'
          : 'forest'
    audio.startMusic(theme, seed)
    const waterHeavy = safeLevel.grid.join('').split('~').length - 1 >= 3
    audio.setUnderwater(safeLevel.biome === 'ice' || waterHeavy)
    const t = window.setTimeout(() => {
      engine.begin()
    }, reducedMotion ? 150 : 700)
    return () => {
      window.clearTimeout(t)
      if (deathTimerRef.current) window.clearTimeout(deathTimerRef.current)
      if (captionTimerRef.current) window.clearTimeout(captionTimerRef.current)
      audio.setUnderwater(false)
      audio.stopMusic()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeLevel.index])

  useAutoFullscreenOnPlay(settings.autoFullscreen)

  useEffect(() => {
    if (snapshot.phase !== 'idle' || !homePos) return
    const dist = Math.abs(snapshot.pos.x - homePos.x) + Math.abs(snapshot.pos.y - homePos.y)
    if (dist > 0 && dist <= 2 && snapshot.slides > 0) speak('near-home')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot.phase, snapshot.pos.x, snapshot.pos.y])

  const doSlide = useCallback(
    (dir: Direction) => {
      if (paused || snapshot.phase !== 'idle') return
      setEvents((prev) => ({ seq: prev.seq, list: prev.list, dir: DIR_VEC[dir] }))
      engine.slide(dir)
    },
    [engine, paused, snapshot.phase],
  )

  useEffect(() => {
    if (snapshot.lastEvents.length) {
      seqRef.current += 1
      const sq = seqRef.current
      setEvents((prev) => ({ seq: sq, list: snapshot.lastEvents, dir: prev.dir }))
    }
  }, [snapshot.lastEvents])

  const handlePowerup = useCallbackRef((id: string) => {
    if (paused) return
    const owned = save.powerups[id] ?? 0
    if (owned <= 0) {
      audio.play('error')
      return
    }
    const consume = () => update((s) => ({ ...s, powerups: { ...s.powerups, [id]: (s.powerups[id] ?? 0) - 1 } }))
    switch (id) {
      case 'shield':
        engine.activateShield()
        consume()
        audio.play('powerup')
        break
      case 'rewind':
        if (engine.undo()) {
          consume()
          audio.play('back')
        } else audio.play('error')
        break
      case 'hint': {
        const dir = engine.getHint()
        if (dir) {
          setHintDir(dir)
          if (effects.visionRange < 2) consume()
          audio.play('powerup')
          window.setTimeout(() => setHintDir(null), 2500 + effects.visionRange * 2500)
        } else audio.play('error')
        break
      }
      case 'magnet':
        if (engine.activateMagnet() > 0) {
          consume()
          audio.play('powerup')
        } else audio.play('error')
        break
      case 'slowmo':
        engine.activateSlowmo()
        consume()
        audio.play('powerup')
        break
      case 'extra_lives':
        update((s) => ({ ...s, economy: { ...s.economy, lives: 5, livesUpdatedAt: Date.now() } }))
        consume()
        audio.play('levelup')
        break
      case 'invincibility':
        engine.activateInvincibility(3)
        consume()
        audio.play('powerup')
        break
      case 'double_coins':
        if (doubleCoinsRef.current) {
          audio.play('error')
          break
        }
        doubleCoinsRef.current = true
        consume()
        audio.play('powerup')
        break
      case 'score_multiplier':
        if (scoreSurgeRef.current) {
          audio.play('error')
          break
        }
        scoreSurgeRef.current = true
        consume()
        audio.play('powerup')
        break
      case 'skip_level':
        if (finishedRef.current) break
        finishedRef.current = true
        consume()
        audio.play('levelup')
        if (endless) {
          window.setTimeout(() => replaceLevel(safeLevel.index + 1), 300)
        } else {
          update((s) => ({
            ...s,
            progress: {
              ...s.progress,
              completedLevels: s.progress.completedLevels.includes(safeLevel.index)
                ? s.progress.completedLevels
                : [...s.progress.completedLevels, safeLevel.index],
            },
          }))
          window.setTimeout(() => go('map'), 300)
        }
        break
      default:
        audio.play('error')
    }
  })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase()
      if (paused) {
        if (e.key === 'Escape' || k === 'p') back()
        return
      }
      if (e.key === 'Escape' || k === 'p') {
        pauseRun()
        go('pause')
        return
      }
      if (k === 'r') {
        if (finishedRef.current) return
        bankRef.current = { coins: 0, gems: 0 }
        engine.respawn()
        resetRun()
        return
      }
      if (k === 'arrowup' || k === 'w') doSlide('up')
      else if (k === 'arrowdown' || k === 's') doSlide('down')
      else if (k === 'arrowleft' || k === 'a') doSlide('left')
      else if (k === 'arrowright' || k === 'd') doSlide('right')
      else if (k === 'z') handlePowerup('rewind')
      else if (['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].includes(k)) {
        const slot = k === '0' ? 9 : Number(k) - 1
        const def = POWERUPS[slot]
        if (def) handlePowerup(def.id)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [back, doSlide, engine, go, handlePowerup, paused, pauseRun, resetRun])

  useEffect(() => {
    if (!paused) resumeRun()
  }, [paused, resumeRun])

  if (!level) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-[var(--text-dim)]">That level isn&apos;t available yet.</p>
        <GameButton icon="arrow-left" onClick={back}>
          Back to Map
        </GameButton>
      </div>
    )
  }

  const hudName = endless ? `${safeLevel.name} · Streak ${endlessRun.streak + 1}` : safeLevel.name

  return (
    <div
      className="relative flex h-full flex-col"
      style={{ background: `radial-gradient(circle at 50% 25%, ${biome.surface}, ${biome.ink} 80%)` }}
    >
      <HUD
        levelName={hudName}
        slides={snapshot.slides}
        stepTarget={safeLevel.stepTarget}
        coins={snapshot.coins + snapshot.gems}
        totalCoins={safeLevel.coinCount + safeLevel.gemCount}
        timeMs={state.timeMs}
        onPause={() => {
          pauseRun()
          go('pause')
        }}
      />

      <div className="relative flex-1 mx-3 my-2 rounded-[var(--r-xl)] overflow-hidden shadow-sunken border border-[var(--edge)]/40">
        <GameBoard3D
          level={safeLevel}
          snapshot={snapshot}
          emberColor={emberColor}
          trailColor={trailColor}
          theme={biome}
          reducedMotion={reducedMotion}
          graphics={graphics}
          events={events}
          shape={character?.shape}
          equippedProps={save.cosmetics.equippedProps}
          bonkKey={bonkKey}
          characterId={character?.id ?? 'default_hero'}
        />

        <AnimatePresence>
          {snapshot.phase === 'intro' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="pointer-events-none absolute inset-0 grid place-items-center"
            >
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="flex flex-col items-center gap-1 rounded-[var(--r-lg)] bg-black/40 px-5 py-3 text-center backdrop-blur"
              >
                <span className="text-sm font-bold text-[var(--text)]">Entering {biome.name}…</span>
                <span className="max-w-[260px] text-xs text-[var(--text-dim)]">{storyCue.line}</span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {caption && (
            <motion.div
              key={caption.key}
              initial={{ opacity: 0, y: -10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.95 }}
              className="pointer-events-none absolute inset-x-4 top-3 z-10 mx-auto max-w-xl rounded-[var(--r-md)] bg-black/55 px-3 py-1.5 text-center text-[10px] font-semibold leading-snug backdrop-blur sm:inset-x-20 sm:px-3.5 sm:py-2 sm:text-sm"
              style={{ color: TONE_COLOR[caption.tone] }}
            >
              {caption.text}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {flash && (
            <motion.div
              key={flash.key}
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  flash.kind === 'death'
                    ? 'radial-gradient(120% 120% at 50% 50%, rgba(255,80,60,0.12) 30%, rgba(255,50,30,0.55) 100%)'
                    : `radial-gradient(120% 120% at 50% 50%, rgba(255,214,140,0.1) 25%, ${emberColor}88 100%)`,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: reducedMotion ? [0, 0.5, 0] : [0, 1, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: reducedMotion ? 0.3 : flash.kind === 'death' ? 0.7 : 0.9, times: [0, 0.25, 1] }}
              onAnimationComplete={() => setFlash(null)}
            />
          )}
        </AnimatePresence>

        {snapshot.slowmo && (
          <motion.div
            className="pointer-events-none absolute inset-0"
            style={{ background: 'radial-gradient(130% 130% at 50% 50%, transparent 45%, rgba(95,214,255,0.22) 100%)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          />
        )}

        {snapshot.invincibleLeft > 0 && (
          <motion.div
            className="pointer-events-none absolute inset-0"
            style={{ background: 'radial-gradient(130% 130% at 50% 50%, transparent 50%, rgba(255,207,92,0.25) 100%)' }}
            initial={{ opacity: 0 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: [0.6, 1, 0.6] }}
            transition={reducedMotion ? undefined : { duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}

        <div className="pointer-events-none absolute top-3 right-3 flex flex-col items-end gap-1.5">
          <AnimatePresence>
            {snapshot.shieldActive && (
              <motion.div
                key="shield"
                initial={{ opacity: 0, x: 12, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 12, scale: 0.9 }}
                className="flex items-center gap-1.5 rounded-full bg-[var(--success)]/90 px-3 py-1 text-xs font-bold text-[#04150c]"
              >
                <PixelIcon name="shield" size={13} />
                Shield ready
              </motion.div>
            )}
            {snapshot.invincibleLeft > 0 && (
              <motion.div
                key="invincible"
                initial={{ opacity: 0, x: 12, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 12, scale: 0.9 }}
                className="flex items-center gap-1.5 rounded-full bg-[var(--coin)]/90 px-3 py-1 text-xs font-bold text-[#231404]"
              >
                <PixelIcon name="invincibility" size={13} />
                Invincible x{snapshot.invincibleLeft}
              </motion.div>
            )}
            {snapshot.slowmo && (
              <motion.div
                key="slowmo"
                initial={{ opacity: 0, x: 12, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 12, scale: 0.9 }}
                className="flex items-center gap-1.5 rounded-full bg-[var(--gem)]/90 px-3 py-1 text-xs font-bold text-[#04222b]"
              >
                <PixelIcon name="clock" size={13} />
                Slow drift
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {hintDir && (
          <div className="pointer-events-none absolute top-3 left-1/2 -translate-x-1/2 rounded-full bg-[var(--ember)] px-4 py-1.5 text-sm font-bold text-[#231404] shadow-glow">
            Slide {hintDir}
          </div>
        )}

        <TouchControls onSlide={doSlide} />
        <TutorialOverlay levelIndex={safeLevel.index} visible={tutorialOpen} onDismiss={() => setTutorialOpen(false)} />
      </div>

      <div className="pb-[calc(0.75rem+var(--safe-bottom))]">
        <PowerupDock onActivate={handlePowerup} />
      </div>
    </div>
  )
}
